import { memo, useCallback, useRef } from "react";
import { DateInput, TextInput } from "@narmi/design_system";
import { DateTime } from "luxon";

const MemoizedDateInput = memo(DateInput) as typeof DateInput;

// Predicate for flatpickr's `disable` config: disables any day after today so
// a future date can't be picked from the calendar or typed into the field.
const disableFutureDates = [
  (date: Date) => DateTime.fromJSDate(date) > DateTime.now().endOf("day"),
];

const mergeRefs =
  (...refs: any[]) =>
  (el: any) =>
    refs.forEach((ref) => {
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    });

// NDS DateInput hardcodes allowInput:true which registers a focus listener that
// reopens the calendar after a date is selected. We patch the flatpickr instance
// to remove the focus listener while keeping click-to-open behavior.
const useClickOnlyRef = () =>
  useCallback((el: HTMLInputElement | null) => {
    if (!el) return;
    setTimeout(() => {
      // eslint-disable-next-line no-underscore-dangle
      const fp = (el as any)._flatpickr;
      if (!fp) return;
      fp.set("clickOpens", false);
      el.addEventListener("click", fp.open);
    }, 0);
  }, []);

interface DateFilterInputProps {
  label: string;
  initialValue: string;
  onChange: (value: string) => void;
  minDate?: string;
  error?: string;
  testId?: string;
}

// NDS's DateInput reinitializes flatpickr from scratch on every render, and a
// controlled `value` prop gets reasserted on each of those renders — stomping
// whatever the user just typed. We seed the field once via `defaultDate` and
// let flatpickr own the DOM value from then on, and wrap it in `memo` with
// stable props so an unrelated parent re-render can't tear down and rebuild
// the underlying flatpickr instance mid-keystroke.
const DateFilterInput = ({
  label,
  initialValue,
  onChange,
  minDate,
  error,
  testId,
}: DateFilterInputProps) => {
  const defaultDateRef = useRef(initialValue);
  const clickOnlyRef = useClickOnlyRef();

  const renderInput = useCallback(
    (props: any, ref: any) => (
      <TextInput {...props} ref={mergeRefs(ref, clickOnlyRef)} type="text" />
    ),
    [clickOnlyRef]
  );

  return (
    <MemoizedDateInput
      label={label}
      defaultDate={defaultDateRef.current}
      minDate={minDate}
      disableDates={disableFutureDates}
      onChange={onChange}
      error={error}
      testId={testId}
      renderInput={renderInput}
    />
  );
};

export default memo(DateFilterInput);
